import { useState, useEffect } from 'react';

/**
 * useWayfinderSync
 * Custom hook to fetch and sync wayfinder data with real-time updates
 * 
 * Usage:
 * const { occupiedDesks, positions, loading, error } = useWayfinderSync('EAST');
 */
function useWayfinderSync(zone = 'EAST', refreshInterval = 180000) {
  const [occupiedDesks, setOccupiedDesks] = useState([]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let intervalId = null;
    let retryTimeoutId = null;

    const fetchWayfinderData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        const response = await fetch(`/api/wayfinder/zones/${zone}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // Handle session timeout by refreshing page
        if (response.status === 401) {
          const result = await response.json();
          if (result.error?.includes('timed out')) {
            console.warn('[Wayfinder] Session timed out, reloading...');
            window.location.reload();
            return;
          }
          throw new Error(result.error || 'Unauthorized');
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setOccupiedDesks(result.data.desks || []);
          setPositions(result.data.positions || {});
          setError(null);
          setRetryCount(0);
          console.log(`[Wayfinder] Synced ${zone}: ${result.data.occupied_desks} occupied desks`);
        } else {
          setError(result.message || 'Failed to fetch wayfinder data');
        }
      } catch (err) {
        console.error(`[Wayfinder] Sync error for ${zone}:`, err);
        setError(`Unable to connect to wayfinder service: ${err.message}`);
        
        // Retry with exponential backoff (max 3 retries)
        if (retryCount < 3) {
          const backoffMs = Math.pow(2, retryCount) * 1000;
          console.log(`[Wayfinder] Retrying in ${backoffMs}ms...`);
          retryTimeoutId = setTimeout(() => {
            setRetryCount(retryCount + 1);
            fetchWayfinderData();
          }, backoffMs);
        }
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchWayfinderData();

    // Set up polling interval
    if (refreshInterval > 0) {
      intervalId = setInterval(fetchWayfinderData, refreshInterval);
    }

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [zone, refreshInterval, retryCount]);

  return {
    occupiedDesks,
    positions,
    loading,
    error
  };
}

export default useWayfinderSync;
